import { graphqlClient } from '@/lib/graphql-client';
import { buildPagingLiteral, buildWhereLiteral } from '@/lib/graphql-query-builder';
import { createApplicationApi } from '@/modules/service-desk/shared/application-api';
import { runServiceDeskWorkflow } from '@/modules/service-desk/shared/application-workflows';
import { toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { NocApplyValues } from '../schemas/noc-apply-form-schema';
import { NocApplication, NocGeneratedData, NocType } from '../types/noc.types';
import { NOC_FIELDS } from '../graphql/noc.fields';

export const nocApi = createApplicationApi<NocApplication>('NocApplication', NOC_FIELDS);

const buildGetNocTypesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    NocTypes: getNocTypes(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        ItemId
        Name
        DocumentBody
      }
    }
  }
`;

export const getNocTypes = async (): Promise<NocType[]> => {
  const result = await graphqlClient.query<{ NocTypes: { items: NocType[] } }>({
    query: buildGetNocTypesQuery({}, 1, 200),
  });
  // The whole list is one page, so it is sorted here by `Name`, like the legacy
  // `SortBy(x => x.Name)` (NocTypeService.cs:152).
  return [...result.NocTypes.items].sort((a, b) => a.Name.localeCompare(b.Name));
};

interface SubmitNocParams {
  existing?: NocApplication;
  values: NocApplyValues;
}

/**
 * Legacy `CreateNocApplicationCommand`, now the NOC_APPLICATION_SUBMIT workflow. It reads the NOC type
 * to decide which travel fields are required and drops the destination of a passport NOC. Travel
 * dates are date-only.
 */
export const submitNocApplication = ({ existing, values }: SubmitNocParams) =>
  runServiceDeskWorkflow('NOC_APPLICATION_SUBMIT', {
    itemId: existing?.ItemId,
    nocTypeId: values.nocTypeId,
    nocCopyType: values.nocCopyType,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    designation: values.designation.trim(),
    passportNumber: values.passportNumber.trim(),
    travelStartDate: toDateOnlyValue(values.startDate) ?? '',
    travelEndDate: toDateOnlyValue(values.endDate) ?? '',
    travelDestination: values.travelDestination.trim(),
    reason: values.reason.trim(),
  });

interface ReviewNocParams {
  itemId: string;
  status: 'Accepted' | 'Rejected';
}

/**
 * Legacy `UpdateNocApplicationStatusAsync` (NOC_APPLICATION_REVIEW workflow): a reviewer cannot decide
 * their own application, only a pending one is decided, and the first acceptance issues the number.
 */
export const reviewNocApplication = ({ itemId, status }: ReviewNocParams) =>
  runServiceDeskWorkflow('NOC_APPLICATION_REVIEW', { itemId, status });

interface GenerateNocParams {
  itemId: string;
  data: NocGeneratedData;
}

/**
 * Legacy `GenerateNocReportAsync` (NOC_APPLICATION_GENERATE workflow): records what was generated in
 * the application's history and marks it Generated. The number is set the first time only.
 */
export const generateNocLetter = ({ itemId, data }: GenerateNocParams) =>
  runServiceDeskWorkflow('NOC_APPLICATION_GENERATE', {
    itemId,
    documentNumber: data.DocumentNumber,
    letterBody: data.LetterBody,
    pocName: data.PocName,
    pocDesignation: data.PocDesignation,
    pocEmail: data.PocEmail,
    pocMobile: data.PocMobile,
  });

/** Legacy `DeleteNocApplicationAsync` as an admin (NOC_APPLICATION_DELETE): pending and rejected only. */
export const deleteNocApplication = (itemId: string) =>
  runServiceDeskWorkflow('NOC_APPLICATION_DELETE', { itemId });
